import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Stack,
  Skeleton
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import useCustomFetch from '../../hooks/useCustomFetch';
import { API_ENDPOINTS } from '../../config/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      iconColor: '#a0a0a0',
      '::placeholder': {
        color: '#6b6b6b'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const BRAND_ICONS = {
  visa: '💳 Visa',
  mastercard: '💳 Mastercard',
  amex: '💳 Amex',
  discover: '💳 Discover',
  diners: '💳 Diners',
  jcb: '💳 JCB',
  unionpay: '💳 UnionPay',
};

function UpdateCardForm({ onSuccess, onCancel }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { put } = useCustomFetch();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      const response = await put(API_ENDPOINTS.SUBSCRIPTIONS.UPDATE_PAYMENT_METHOD, {
        paymentMethodId: paymentMethod.id
      });

      if (response.data?.success) {
        onSuccess(response.data.data);
      } else {
        throw new Error(response.data?.message || t('billing.updateError'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('billing.updateError'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ 
        p: 2.5, borderRadius: 2, 
        border: '1px solid rgba(255,255,255,0.15)', 
        bgcolor: 'rgba(255,255,255,0.03)',
        mb: 2
      }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Stack direction="row" spacing={2}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!stripe || processing}
          startIcon={processing ? <CircularProgress size={18} /> : <CreditCardIcon />}
        >
          {processing ? t('billing.updating') : t('billing.saveCard')}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={processing}>
          {t('common.cancel')}
        </Button>
      </Stack>
    </Box>
  );
}

function BillingSettings() {
  const { t } = useTranslation();
  const { get } = useCustomFetch();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchBilling = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await get(API_ENDPOINTS.SUBSCRIPTIONS.BILLING);
      if (response.data?.success) {
        setBilling(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('billing.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
  }, []);

  const handleCardUpdated = (newCardData) => {
    setBilling(prev => ({
      ...prev,
      paymentMethod: newCardData
    }));
    setShowUpdateForm(false);
    setSuccessMsg(t('billing.updateSuccess'));
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const getStatusChip = (status) => {
    const statusMap = {
      active: { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, label: t('billing.status.active') },
      trialing: { color: 'info', icon: <AutorenewIcon sx={{ fontSize: 16 }} />, label: t('billing.status.trialing') },
      past_due: { color: 'warning', icon: <WarningIcon sx={{ fontSize: 16 }} />, label: t('billing.status.pastDue') },
      canceled: { color: 'error', icon: <WarningIcon sx={{ fontSize: 16 }} />, label: t('billing.status.canceled') },
      incomplete: { color: 'warning', icon: <WarningIcon sx={{ fontSize: 16 }} />, label: t('billing.status.incomplete') },
    };
    const s = statusMap[status] || { color: 'default', label: status };
    return <Chip icon={s.icon} label={s.label} color={s.color} size="small" sx={{ fontWeight: 600 }} />;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp * 1000).toLocaleDateString(undefined, { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }}>{successMsg}</Alert>
      )}

      {/* Subscription Info */}
      <Paper
        elevation={0}
        sx={{
          p: 3, mb: 3, borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.1)',
          background: 'linear-gradient(135deg, rgba(138,43,226,0.08) 0%, rgba(255,64,129,0.05) 100%)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('billing.subscriptionTitle')}
          </Typography>
          {billing?.subscription && getStatusChip(billing.subscription.status)}
        </Stack>

        {billing?.subscription ? (
          <Box>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                ${billing.subscription.plan.amount}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                /{billing.subscription.plan.interval === 'month' ? t('billing.perMonth') : billing.subscription.plan.interval}
              </Typography>
            </Stack>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

            <Stack spacing={1}>
              {billing.subscription.trialEnd && billing.subscription.status === 'trialing' && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{t('billing.trialEnds')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(billing.subscription.trialEnd)}
                  </Typography>
                </Stack>
              )}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{t('billing.nextBilling')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDate(billing.subscription.currentPeriodEnd)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('billing.noSubscription')}
          </Typography>
        )}
      </Paper>

      {/* Payment Method */}
      <Paper
        elevation={0}
        sx={{
          p: 3, borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('billing.paymentMethodTitle')}
          </Typography>
          {billing?.paymentMethod && !showUpdateForm && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreditCardIcon />}
              onClick={() => setShowUpdateForm(true)}
              sx={{ borderRadius: 2 }}
            >
              {t('billing.changeCard')}
            </Button>
          )}
        </Stack>

        {showUpdateForm ? (
          <Elements stripe={stripePromise}>
            <UpdateCardForm 
              onSuccess={handleCardUpdated} 
              onCancel={() => setShowUpdateForm(false)} 
            />
          </Elements>
        ) : billing?.paymentMethod ? (
          <Box
            sx={{
              p: 2.5, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: 'primary.main', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.3rem'
            }}>
              <CreditCardIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {BRAND_ICONS[billing.paymentMethod.brand] || `💳 ${billing.paymentMethod.brand}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •••• •••• •••• {billing.paymentMethod.last4} — {t('billing.expires')} {String(billing.paymentMethod.expMonth).padStart(2, '0')}/{billing.paymentMethod.expYear}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('billing.noPaymentMethod')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<CreditCardIcon />}
              onClick={() => setShowUpdateForm(true)}
              sx={{ borderRadius: 2 }}
            >
              {t('billing.addCard')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default BillingSettings;
